import { useEffect, useState } from "react";
import { fetchPageContent } from "../api/content";
import {
  fetchPresskit,
  formatDimensions,
  formatFileSize,
  presskitObjectUrl,
  presskitThumbnailUrl,
} from "../api/presskit";
import { defaultPresskitContent } from "../content/defaultContent";
import "./css/PressKit.css";

const PressKit = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [content, setContent] = useState(defaultPresskitContent);

  const loadFiles = () => {
    setLoading(true);
    setError('');
    fetchPresskit()
      .then(setAssets)
      .catch((requestError) => {
        console.error(requestError);
        setError('Press kit media is temporarily unavailable. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadFiles();
    fetchPageContent('presskit')
      .then((updatedContent) => setContent({ ...defaultPresskitContent, ...updatedContent }))
      .catch((contentError) => console.error('Unable to load updated Press Kit content.', contentError));
    // The press-kit listing is loaded once when this page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const photos = assets.filter((asset) => asset.mediaType === 'image');
  const videos = assets.filter((asset) => asset.mediaType === 'video');

  return (
    <main className="press-kit-container">
      <section className="press-kit-hero">
        <p className="press-kit-kicker">{content.kicker}</p>
        <h1 className="page-title">{content.title}</h1>
        <p>{content.intro}</p>
      </section>

      <section className="press-kit-section">
        <div className="press-kit-section-header">
          <h2>Photos</h2>
          <span>{loading ? "Loading" : error ? "Unavailable" : `${photos.length} files`}</span>
        </div>

        {loading ? (
          <div className="image-grid" aria-label="Loading press kit photos">
            {Array.from({ length: 6 }).map((_, index) => (
              <div className="image-skeleton" key={index}></div>
            ))}
          </div>
        ) : error ? (
          <div className="press-kit-message" role="alert">
            <p>{error}</p>
            <button type="button" onClick={loadFiles}>Try again</button>
          </div>
        ) : photos.length === 0 ? (
          <p className="press-kit-message" role="status">No press kit photos are currently available.</p>
        ) : (
          <div className="image-grid">
            {photos.map((asset) => (
              <article className="press-kit-media-card" key={asset.id}>
                <a className="press-kit-image-link" href={presskitObjectUrl(asset, true)} aria-label={`Download ${asset.altText}`}>
                  <img src={presskitThumbnailUrl(asset)} alt={asset.altText} loading="lazy" decoding="async" width="480" height="480" />
                </a>
                <div className="press-kit-media-details">
                  <span>{formatDimensions(asset)} · {formatFileSize(asset.sizeBytes)}</span>
                  <a href={presskitObjectUrl(asset, true)}>Download original</a>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="press-kit-section">
        <div className="press-kit-section-header">
          <h2>Videos</h2>
          <span>{loading ? "Loading" : error ? "Unavailable" : `${videos.length} files`}</span>
        </div>

        {loading ? (
          <p className="press-kit-message" role="status">Loading approved videos…</p>
        ) : error ? (
          <p className="press-kit-message">Approved videos could not be loaded.</p>
        ) : videos.length === 0 ? (
          <p className="press-kit-message" role="status">No press kit videos are currently available.</p>
        ) : (
          <div className="video-grid">
            {videos.map((asset) => (
              <article className="press-kit-video-card" key={asset.id}>
                <video controls preload="metadata" aria-label={asset.altText}>
                  <source src={presskitObjectUrl(asset)} type={asset.mimeType} />
                  Your browser does not support this video.
                </video>
                <div className="press-kit-video-details">
                  <p>{asset.altText}</p>
                  <span>{formatDimensions(asset)} · {formatFileSize(asset.sizeBytes)}</span>
                  <a href={presskitObjectUrl(asset, true)}>Download video</a>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="bio-section bio-section-featured">
        <h2>Short Bio</h2>
        <p className="bio-text">{content.shortBio}</p>
      </section>

      <section className="bio-section">
        <h2>Long Bio</h2>
        <div className="bio-text">
          {content.longBioParagraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
        </div>
      </section>
    </main>
  );
};

export default PressKit;
